import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { FamilyHistoryFormValues } from "./interface";
import useGetFamilyHistory from "hooks/api/client/useGetFamilyHistory";
import useUpsertFamilyHistory from "hooks/api/client/useUpsertFamilyHistory";

const EMPTY_VALUES: FamilyHistoryFormValues = {
  caregiverMainConcerns: "",
  dailyRoutineStructure: "",
  familyCaregiversInHome: "",
};

export const FamilyHistory: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { familyHistory, refetchFamilyHistory } = useGetFamilyHistory(clientIdNum);

  const { upsertFamilyHistoryMutation, isUpsertFamilyHistoryLoading } = useUpsertFamilyHistory({
    onSuccess: () => {
      refetchFamilyHistory();
      setEditMode(false);
    },
  });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<FamilyHistoryFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<FamilyHistoryFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (familyHistory) {
      setFormValues(familyHistory);
      setInitialValues(familyHistory);
    }
  }, [familyHistory]);

  const handleChange =
    (field: keyof FamilyHistoryFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    upsertFamilyHistoryMutation({ clientId: clientIdNum, payload: formValues });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isUpsertFamilyHistoryLoading;

  return (
    <SectionWrapper
      title="Family History"
      description="Household composition, caregiver priorities, daily routines, and family health context that shape the client's care environment."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default FamilyHistory;
